// (c) chaehongee. Confidential and proprietary.

import { useCallback, useRef } from 'react'
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react'
import type { PointerDelegates, SnapshotData } from './types'
import type { ToolMode } from '../toolbar/useToolMode'
import { useCanvasHistory } from './useCanvasHistory'
import { useWorldTransform } from './useWorldTransform'

interface UseCanvasViewstateOptions {
    containerRef: RefObject<HTMLDivElement | null>
    getBrushEnabled: () => boolean
    getToolMode: () => ToolMode
    onCanvasBackgroundClick: () => void
    getSnapshotData: () => SnapshotData
    applySnapshotData: (snap: SnapshotData) => void
}

export function useCanvasViewstate(options: UseCanvasViewstateOptions) {
    const {
        containerRef,
        getBrushEnabled,
        getToolMode,
        onCanvasBackgroundClick,
        getSnapshotData,
        applySnapshotData,
    } = options

    const {
        contentScale,
        setContentScale,
        contentOffset,
        setContentOffset,
        clientToWorld,
        onWheel,
        startPan,
        startForcePan,
        movePan,
        endPan,
        isPanning,
        spacePressed,
    } = useWorldTransform(containerRef)

    const delegatesRef = useRef<PointerDelegates>({})

    const getSnapshot = useCallback((): SnapshotData => {
        return getSnapshotData()
    }, [getSnapshotData])

    const applySnapshot = useCallback((snap: SnapshotData) => {
        applySnapshotData(snap)
    }, [applySnapshotData])

    const history = useCanvasHistory({
        getSnapshot,
        applySnapshot,
    })

    const { beginCapture, commitIfChanged } = history

    const setPointerDelegates = useCallback((delegates: PointerDelegates) => {
        delegatesRef.current = delegates
    }, [])

    const withHistoryCapture = useCallback(<T extends (...args: any[]) => void>(fn: T): T => {
        return ((...args: Parameters<T>) => {
            beginCapture()
            return fn(...args)
        }) as T
    }, [beginCapture])

    const withInstantHistoryCapture = useCallback(<T extends (...args: any[]) => void>(fn: T): T => {
        return ((...args: Parameters<T>) => {
            beginCapture()
            const result = fn(...args)
            commitIfChanged()
            return result
        }) as T
    }, [beginCapture, commitIfChanged])

    const handlePointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
        if (getBrushEnabled()) return
        const target = e.target as Element | null
        if (target && target.closest('[data-canvas-ui="true"]')) return

        // Space+drag or middle-button ALWAYS pans, regardless of tool mode.
        const started = startPan(e)
        if (started) return

        // Grab mode: any left-click pans (even on items, since items will have pointerEvents:none in grab mode)
        if (getToolMode() === 'grab' && e.button === 0) {
            startForcePan(e)
            return
        }

        // Left-click on background (not on an item): start marquee in pointer/select mode
        if (e.button === 0) {
            const isOnItem = target && target.closest('[data-world-item="true"]')
            if (!isOnItem && (getToolMode() === 'pointer' || getToolMode() === 'select')) {
                delegatesRef.current.marquee?.startMarquee?.(e)
                return
            }
        }

        // Fallback: background click deselects
        if (e.button === 0) {
            delegatesRef.current.world?.clearLocalSelection?.()
            onCanvasBackgroundClick()
        }
    }, [getBrushEnabled, getToolMode, startPan, startForcePan, onCanvasBackgroundClick])

    const handlePointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
        if (getBrushEnabled()) return
        if (delegatesRef.current.marquee?.moveMarquee?.(e)) return
        if (delegatesRef.current.world?.onPointerMove?.(e)) return
        if (delegatesRef.current.frame?.onPointerMove?.(e)) return
        movePan(e)
    }, [getBrushEnabled, movePan])

    const handlePointerUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
        if (getBrushEnabled()) return
        const marqueeHandled = delegatesRef.current.marquee?.endMarquee?.(e) ?? false
        if (marqueeHandled) return
        const worldHandled = delegatesRef.current.world?.onPointerUp?.(e) ?? false
        const frameHandled = delegatesRef.current.frame?.onPointerUp?.(e) ?? false
        if (worldHandled || frameHandled) {
            commitIfChanged()
            return
        }
        endPan(e)
    }, [getBrushEnabled, endPan, commitIfChanged])

    return {
        contentScale,
        setContentScale,
        contentOffset,
        setContentOffset,
        clientToWorld,
        onWheel,
        isPanning,
        spacePressed,
        history,
        withHistoryCapture,
        withInstantHistoryCapture,
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        setPointerDelegates,
    }
}
